import { kvGet, kvSet } from '../infrastructure/storage/kvAccess';

/**
 * IDTracker Service - Tracking und Monitoring für französische IDs
 * Phase 4: Monitoring und Optimierung
 * 
 * Funktionen:
 * - ID-Historie verfolgen
 * - Metriken sammeln
 * - Kollisions-Tracking
 * - Performance-Monitoring
 */

class IDTracker {
  constructor() {
    this.history = new Map();
    this.metrics = {
      totalGenerated: 0,
      collisions: 0,
      averageGenerationTime: 0,
      typeDistribution: new Map(),
      businessDistribution: new Map(),
      dailyStats: new Map(),
      lastReset: Date.now()
    };
    this.initializeStorage();
  }

  /**
   * Initialisiert den lokalen Speicher für ID-Tracking
   */
  initializeStorage() {
    try {
      const stored = kvGet('id_tracker_data');
      if (stored) {
        const data = typeof stored === 'string' ? JSON.parse(stored) : stored;
        this.history = new Map(data.history || []);
        
        // Restore metrics with proper Map initialization
        this.metrics = {
          totalGenerated: data.metrics?.totalGenerated || 0,
          collisions: data.metrics?.collisions || 0,
          averageGenerationTime: data.metrics?.averageGenerationTime || 0,
          typeDistribution: new Map(data.metrics?.typeDistribution || []),
          businessDistribution: new Map(data.metrics?.businessDistribution || []),
          dailyStats: new Map((data.metrics?.dailyStats || []).map(([date, stats]) => [
            date,
            {
              ...stats,
              byType: new Map(stats.byType || [])
            }
          ])),
          lastReset: data.metrics?.lastReset || Date.now()
        };
      }
    } catch (error) {
      console.warn('IDTracker: Could not load stored data:', error);
    }
  }

  /**
   * Speichert ID-Tracking-Daten im lokalen Speicher
   */
  saveToStorage() {
    try {
      // Convert dailyStats with nested Maps
      const dailyStatsArray = Array.from(this.metrics.dailyStats.entries()).map(([date, stats]) => [
        date,
        {
          ...stats,
          byType: Array.from(stats.byType.entries())
        }
      ]);
      
      const data = {
        history: Array.from(this.history.entries()),
        metrics: {
          ...this.metrics,
          typeDistribution: Array.from(this.metrics.typeDistribution.entries()),
          businessDistribution: Array.from(this.metrics.businessDistribution.entries()),
          dailyStats: dailyStatsArray
        },
        lastSaved: Date.now()
      };
      kvSet('id_tracker_data', data);
    } catch (error) {
      console.warn('IDTracker: Could not save data:', error);
    }
  }

  /**
   * Verfolgt eine generierte ID
   * @param {string} id - Die generierte ID
   * @param {string} type - Der ID-Typ (vente, achat, client, etc.)
   * @param {Object} metadata - Zusätzliche Metadaten
   */
  trackId(id, type, metadata = {}) {
    const timestamp = Date.now();
    const entry = {
      id,
      type,
      timestamp,
      businessId: metadata.businessId || 'unknown',
      userId: metadata.userId || 'unknown',
      source: metadata.source || 'unknown',
      generationTime: metadata.generationTime || 0,
      retries: metadata.retries || 0,
      ...metadata
    };

    // Historie aktualisieren
    if (!this.history.has(type)) {
      this.history.set(type, []);
    }
    this.history.get(type).push(entry);

    // Nur die letzten 1000 Einträge pro Typ behalten
    const typeHistory = this.history.get(type);
    if (typeHistory.length > 1000) {
      typeHistory.splice(0, typeHistory.length - 1000);
    }

    // Metriken aktualisieren
    this.updateMetrics(entry);

    // In lokalen Speicher speichern (alle 10 Einträge)
    if (this.metrics.totalGenerated % 10 === 0) {
      this.saveToStorage();
    }

    console.log(`📊 IDTracker: Tracked ${type.toUpperCase()} ID: ${id}`);
  }

  /**
   * Aktualisiert die Metriken basierend auf einem neuen Eintrag
   * @param {Object} entry - Der ID-Eintrag
   */
  updateMetrics(entry) {
    this.metrics.totalGenerated++;

    // Typ-Verteilung
    const typeCount = this.metrics.typeDistribution.get(entry.type) || 0;
    this.metrics.typeDistribution.set(entry.type, typeCount + 1);

    // Business-Verteilung
    const businessCount = this.metrics.businessDistribution.get(entry.businessId) || 0;
    this.metrics.businessDistribution.set(entry.businessId, businessCount + 1);

    // Tägliche Statistiken
    const today = new Date().toDateString();
    if (!this.metrics.dailyStats.has(today)) {
      this.metrics.dailyStats.set(today, {
        total: 0,
        byType: new Map(),
        averageTime: 0,
        collisions: 0
      });
    }
    const dailyStats = this.metrics.dailyStats.get(today);
    dailyStats.total++;
    
    // Ensure byType is a Map
    if (!dailyStats.byType || typeof dailyStats.byType.get !== 'function') {
      dailyStats.byType = new Map();
    }
    
    const dailyTypeCount = dailyStats.byType.get(entry.type) || 0;
    dailyStats.byType.set(entry.type, dailyTypeCount + 1);

    // Durchschnittliche Generierungszeit
    if (entry.generationTime > 0) {
      const currentAvg = this.metrics.averageGenerationTime;
      const total = this.metrics.totalGenerated;
      this.metrics.averageGenerationTime = 
        (currentAvg * (total - 1) + entry.generationTime) / total;
    }

    // Kollisionen zählen
    if (entry.retries > 0) {
      this.metrics.collisions++;
      dailyStats.collisions++;
    }
  }

  /**
   * Gibt die aktuellen Metriken zurück
   * @returns {Object} Metriken-Objekt
   */
  getMetrics() {
    return {
      ...this.metrics,
      typeDistribution: Object.fromEntries(this.metrics.typeDistribution),
      businessDistribution: Object.fromEntries(this.metrics.businessDistribution),
      dailyStats: Object.fromEntries(
        Array.from(this.metrics.dailyStats.entries()).map(([date, stats]) => [
          date,
          {
            ...stats,
            byType: Object.fromEntries(stats.byType)
          }
        ])
      )
    };
  }

  /**
   * Gibt die Historie für einen bestimmten Typ zurück
   * @param {string} type - Der ID-Typ
   * @param {number} limit - Maximale Anzahl von Einträgen
   * @returns {Array} Array von ID-Einträgen
   */
  getHistory(type, limit = 100) {
    const typeHistory = this.history.get(type) || [];
    return typeHistory.slice(-limit);
  }

  /**
   * Gibt alle verfügbaren Typen zurück
   * @returns {Array} Array von ID-Typen
   */
  getAvailableTypes() {
    return Array.from(this.history.keys());
  }

  /**
   * Berechnet einen Health-Score für das ID-System
   * @returns {Object} Health-Score und Empfehlungen
   */
  getHealthScore() {
    const metrics = this.getMetrics();
    const totalGenerated = metrics.totalGenerated;
    const collisionRate = totalGenerated > 0 ? (metrics.collisions / totalGenerated) * 100 : 0;
    const avgGenerationTime = metrics.averageGenerationTime;

    let score = 100;
    let issues = [];
    let recommendations = [];

    // Kollisionsrate bewerten
    if (collisionRate > 5) {
      score -= 20;
      issues.push(`Hohe Kollisionsrate: ${collisionRate.toFixed(2)}%`);
      recommendations.push('ID-Generierung optimieren oder längere Zufallszahlen verwenden');
    } else if (collisionRate > 1) {
      score -= 10;
      issues.push(`Moderate Kollisionsrate: ${collisionRate.toFixed(2)}%`);
    }

    // Generierungszeit bewerten
    if (avgGenerationTime > 100) {
      score -= 15;
      issues.push(`Langsame ID-Generierung: ${avgGenerationTime.toFixed(2)}ms`);
      recommendations.push('ID-Pools implementieren für bessere Performance');
    } else if (avgGenerationTime > 50) {
      score -= 5;
      issues.push(`Moderate Generierungszeit: ${avgGenerationTime.toFixed(2)}ms`);
    }

    // Typ-Verteilung bewerten
    const typeCount = Object.keys(metrics.typeDistribution).length;
    if (typeCount < 3) {
      score -= 5;
      issues.push('Wenige ID-Typen in Verwendung');
      recommendations.push('Weitere ID-Typen implementieren');
    }

    // Tägliche Aktivität bewerten
    const today = new Date().toDateString();
    const todayStats = metrics.dailyStats[today];
    if (todayStats && todayStats.total > 1000) {
      score -= 10;
      issues.push('Hohe tägliche ID-Generierung');
      recommendations.push('Performance-Optimierung für hohe Volumina');
    }

    return {
      score: Math.max(0, Math.min(100, score)),
      issues,
      recommendations,
      metrics: {
        totalGenerated,
        collisionRate: collisionRate.toFixed(2),
        averageGenerationTime: avgGenerationTime.toFixed(2),
        typeCount,
        dailyGenerated: todayStats?.total || 0
      }
    };
  }

  /**
   * Setzt alle Metriken zurück
   */
  resetMetrics() {
    this.history.clear();
    this.metrics = {
      totalGenerated: 0,
      collisions: 0,
      averageGenerationTime: 0,
      typeDistribution: new Map(),
      businessDistribution: new Map(),
      dailyStats: new Map(),
      lastReset: Date.now()
    };
    this.saveToStorage();
    console.log('📊 IDTracker: Metrics reset');
  }

  /**
   * Exportiert Tracking-Daten für Backup
   * @returns {Object} Exportierte Daten
   */
  exportData() {
    return {
      history: Array.from(this.history.entries()),
      metrics: this.getMetrics(),
      exportDate: new Date().toISOString(),
      version: '1.0.0'
    };
  }

  /**
   * Importiert Tracking-Daten aus Backup
   * @param {Object} data - Zu importierende Daten
   */
  importData(data) {
    try {
      if (data.history) {
        this.history = new Map(data.history);
      }
      if (data.metrics) {
        this.metrics = { ...this.metrics, ...data.metrics };
        // Maps wiederherstellen
        this.metrics.typeDistribution = new Map(Object.entries(data.metrics.typeDistribution || {}));
        this.metrics.businessDistribution = new Map(Object.entries(data.metrics.businessDistribution || {}));
        this.metrics.dailyStats = new Map(Object.entries(data.metrics.dailyStats || {}));
      }
      this.saveToStorage();
      console.log('📊 IDTracker: Data imported successfully');
    } catch (error) {
      console.error('IDTracker: Error importing data:', error);
    }
  }
}

// Singleton-Instanz erstellen
const idTracker = new IDTracker();

export default idTracker;
